import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Component that renders an icon - accepts a className. This matches both
 * an `AppIcon` (wrapped with `emptyStateIcon`) and any plain React component.
 */
export type EmptyStateIcon = React.ComponentType<{ className?: string }>;

/**
 * EmptyState - unified empty-state primitive.
 *
 * Use everywhere the app currently renders plain "No results" / "No leads" /
 * "No folders" text. A designed empty state conveys that the surface is
 * working, that the absence is expected, and that there's something the user
 * can do about it.
 *
 * Sizes:
 * - `sm`: fits inside a popover or small panel (icon 32px)
 * - `md`: default for table bodies and drawer content (icon 48px)
 * - `lg`: full-page empty states (icon 64px)
 */
export interface EmptyStateProps {
  icon?: EmptyStateIcon;
  headline: string;
  description?: React.ReactNode;
  /** Content under the headline, above the action (a setup list, a status card). */
  children?: React.ReactNode;
  action?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function EmptyState({
  icon: Icon,
  headline,
  description,
  children,
  action,
  size = 'md',
  className,
}: EmptyStateProps) {
  const iconSize =
    size === 'sm' ? 'h-8 w-8' : size === 'lg' ? 'h-16 w-16' : 'h-12 w-12';
  const gap = size === 'sm' ? 'gap-1.5' : 'gap-2';
  const padding =
    size === 'sm' ? 'px-4 py-6' : size === 'lg' ? 'px-8 py-16' : 'px-6 py-10';
  const headlineSize =
    size === 'sm' ? 'text-[13px]' : size === 'lg' ? 'text-base' : 'text-sm';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        padding,
        gap,
        className
      )}
    >
      {Icon && (
        <div
          className={cn(
            'flex items-center justify-center rounded-full bg-muted/50 text-muted-foreground',
            size === 'sm'
              ? 'h-10 w-10'
              : size === 'lg'
                ? 'h-20 w-20'
                : 'h-14 w-14'
          )}
        >
          <Icon className={cn(iconSize, 'opacity-70')} />
        </div>
      )}
      <div
        className={cn(
          'flex flex-col',
          size === 'sm' ? 'mt-1 gap-0.5' : 'mt-2 gap-1'
        )}
      >
        <p className={cn(headlineSize, 'font-semibold text-foreground')}>
          {headline}
        </p>
        {description && (
          <p
            className={cn(
              'max-w-sm text-muted-foreground',
              size === 'sm'
                ? 'text-[12px] leading-[16px]'
                : 'text-[13px] leading-[18px]'
            )}
          >
            {description}
          </p>
        )}
      </div>
      {children}
      {action && (
        <div className={cn(size === 'sm' ? 'mt-2' : 'mt-4')}>{action}</div>
      )}
    </div>
  );
}
